import { zodResolver } from "@hookform/resolvers/zod";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";

import { sourcesApiSlice } from "api/sourcesApi.slice";
import { logger } from "common/utilities/logger";
import { useGo } from "common/utilities/useGo";
import { deactivateModal } from "domains/modal/actions";
import {
  COUNTRY_TO_REGION_MAPPING,
  REGION_TO_GREATER_REGION_MAPPING,
} from "domains/moderate/sources/components/EditSourceForm/constants";
import { type SourceFormData, sourceFormDataSchema } from "domains/moderate/sources/components/types";
import { type CountryValue, GREATER_REGION_OPTIONS, REGION_OPTIONS } from "domains/moderate/sources/constants";

export const useAddSourceForm = () => {
  const dispatch = useDispatch();
  const go = useGo();

  const [createSource, { isLoading, error }] = sourcesApiSlice.endpoints.createSource.useMutation();

  const { control, handleSubmit, setValue } = useForm({
    resolver: zodResolver(sourceFormDataSchema),
    defaultValues: {
      description: "",
      active: false,
    },
  });

  const setRegionAndGreaterRegionOnCountryChange = (country: CountryValue) => {
    const associatedRegion = COUNTRY_TO_REGION_MAPPING[country];
    const associatedGreaterRegion = REGION_TO_GREATER_REGION_MAPPING[associatedRegion];

    setValue(
      "region",
      REGION_OPTIONS.find((option) => option.value === associatedRegion),
    );

    setValue(
      "greaterRegion",
      GREATER_REGION_OPTIONS.find((option) => option.value === associatedGreaterRegion),
    );
  };
  const onSubmit: SubmitHandler<SourceFormData> = async (data) => {
    if (isLoading) return;
    const result = await createSource({
      name: data.name,
      url: data.url,
      description: data.description,
      active: data.active,
      logoUrl: data.logoUrl,
      license: data.license?.value,
      autopilot: data.autopilot?.value ?? null,
      type: data.type?.value,
      publicationType: data.publicationType?.value,
      contentType: data.contentType?.value,
      country: data.country?.value,
      region: data.region?.value,
      greaterRegion: data.greaterRegion?.value,
    });
    if (result.error) {
      logger.error("[EditSourceForm] Error updating source", error);
      return;
    }
    dispatch(deactivateModal());
    go(`/moderate/sources/${result.data.id}`);
  };

  return {
    control,
    handleSubmit,
    isLoading,
    error,
    onSubmit,
    setRegionAndGreaterRegionOnCountryChange,
  };
};
